import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";

import type { AppSummary, AppsResponse } from "../../shared/schemas";
import type { WorkspaceAdminResponse } from "../../shared/schemas";
import { navigateToWorkspace, updateAppPreference } from "../api/client";
import { appsQuery, meQuery, workspaceQuery } from "../api/queries";
import { AuthenticatedShell } from "../components/AuthenticatedShell";
import { OfflineState, useOnlineStatus } from "../components/OfflineState";
import { RequestToolDialog } from "../components/RequestToolDialog";
import { useShellViewMode } from "../components/ShellViewModeProvider";
import { Input } from "../components/ui/input";
import { resolveShellOrganizationId, resolveShellViewMode } from "../lib/organization-context";
import { formatDashboardDate, formatDashboardGreeting } from "../lib/tool-recency";
import { dashboardRoute } from "../router";
import { DashboardTools } from "./dashboard/DashboardTools";

function usePinMutation(organizationId: string, onError: () => void) {
  const queryClient = useQueryClient();
  const catalogQuery = appsQuery(organizationId);
  return useMutation({
    mutationFn: ({ appInstanceId, pinned }: { appInstanceId: string; pinned: boolean }) =>
      updateAppPreference(appInstanceId, { pinned }),
    onMutate: async ({ appInstanceId, pinned }) => {
      await queryClient.cancelQueries({ queryKey: catalogQuery.queryKey });
      const previous = queryClient.getQueryData<AppsResponse>(catalogQuery.queryKey);
      queryClient.setQueryData<AppsResponse>(catalogQuery.queryKey, (current) =>
        current
          ? {
              ...current,
              apps: current.apps.map((app) =>
                app.appInstanceId === appInstanceId ? { ...app, pinned } : app,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(catalogQuery.queryKey, context.previous);
      onError();
    },
  });
}

function filterApps(apps: AppSummary[] | undefined, search: string): AppSummary[] {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  return (apps ?? []).filter((app) =>
    `${app.displayName} ${app.description}`.toLocaleLowerCase().includes(normalizedSearch),
  );
}

interface DashboardContentProps {
  actions: {
    isOnline: boolean;
    isPinPending: (appInstanceId: string) => boolean;
    onTogglePin: (app: AppSummary) => void;
  };
  apps: AppsResponse | undefined;
  canManageWorkspace: boolean;
  filteredApps: AppSummary[];
  isError: boolean;
  isOnline: boolean;
  isPending: boolean;
  now: Date;
  organizationId: string;
  organizationName: string | undefined;
  pinError: string | null;
  search: string;
  userDisplayName: string | undefined;
  workspace: WorkspaceAdminResponse | undefined;
  onOrganizationChange: (organizationId: string) => void;
  onSearchChange: (value: string) => void;
}

function DashboardContent(props: DashboardContentProps) {
  return (
    <AuthenticatedShell
      activeOrganizationId={props.organizationId}
      onOrganizationChange={props.onOrganizationChange}
    >
      <main id="main-content" className="workspace-main mx-auto w-full max-w-[1240px]">
        {!props.isOnline ? (
          <OfflineState
            className="mb-7"
            description="CCBox tools need an internet connection. Reconnect to Wi-Fi or cellular data before opening one or changing workspaces."
            variant="banner"
          />
        ) : null}
        <header className="grid gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <p className="mb-1 text-sm text-muted-foreground">{formatDashboardDate(props.now)}</p>
            <h1 className="m-0 font-heading text-3xl leading-tight font-bold tracking-[-0.025em] sm:text-4xl">
              {formatDashboardGreeting(props.now, props.userDisplayName)}
            </h1>
          </div>
          {props.canManageWorkspace ? (
            <RequestToolDialog
              isOnline={props.isOnline}
              organizationId={props.organizationId}
              organizationName={props.organizationName}
            />
          ) : null}
        </header>
        <div className="relative mt-7 max-w-md">
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            aria-label="Find a tool"
            className="pl-9"
            placeholder="Find a tool…"
            type="search"
            value={props.search}
            onChange={(event) => {
              props.onSearchChange(event.currentTarget.value);
            }}
          />
        </div>
        {props.pinError ? (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {props.pinError}
          </p>
        ) : null}
        <DashboardTools
          actions={props.actions}
          apps={props.apps}
          canManageWorkspace={props.canManageWorkspace}
          filteredApps={props.filteredApps}
          isError={props.isError}
          isPending={props.isPending}
          organizationId={props.organizationId}
          search={props.search}
          workspace={props.workspace}
        />
      </main>
    </AuthenticatedShell>
  );
}

export function DashboardPage() {
  const isOnline = useOnlineStatus();
  const me = useQuery({ ...meQuery, enabled: isOnline });
  const search = dashboardRoute.useSearch();
  const { consultantViewMode } = useShellViewMode();
  const [toolSearch, setToolSearch] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const organizationId = resolveShellOrganizationId(me.data, search.org);
  const catalog = useQuery({
    ...appsQuery(organizationId),
    enabled: isOnline && Boolean(organizationId),
  });
  const organization = me.data?.organizations.find(({ id }) => id === organizationId);
  const canManageWorkspace =
    resolveShellViewMode(me.data?.user, organization, consultantViewMode) === "admin";
  const workspace = useQuery({
    ...workspaceQuery(organizationId),
    enabled: isOnline && Boolean(organizationId) && canManageWorkspace,
  });
  const pinMutation = usePinMutation(organizationId, () => {
    setPinError("That pin could not be saved. Please try again.");
  });
  const now = useMemo(() => new Date(), []);
  const filteredApps = filterApps(catalog.data?.apps, toolSearch);

  if (me.data && organizationId && search.org !== organizationId) {
    return <Navigate replace search={{ org: organizationId }} to="/" />;
  }

  const actions = {
    isOnline,
    isPinPending: (appInstanceId: string) =>
      pinMutation.isPending && pinMutation.variables?.appInstanceId === appInstanceId,
    onTogglePin: (app: AppSummary) => {
      setPinError(null);
      pinMutation.mutate({ appInstanceId: app.appInstanceId, pinned: !app.pinned });
    },
  };
  return (
    <DashboardContent
      actions={actions}
      apps={catalog.data}
      canManageWorkspace={canManageWorkspace}
      filteredApps={filteredApps}
      isError={catalog.isError}
      isOnline={isOnline}
      isPending={catalog.isPending}
      now={now}
      organizationId={organizationId}
      organizationName={organization?.name}
      pinError={pinError}
      search={toolSearch}
      userDisplayName={me.data?.user.displayName}
      workspace={workspace.data}
      onOrganizationChange={(nextId) => {
        navigateToWorkspace(nextId, `/?org=${encodeURIComponent(nextId)}`);
      }}
      onSearchChange={setToolSearch}
    />
  );
}
